import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, AlertTriangle, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useFormat } from "../contexts/PrefsContext";

const DISMISS_KEY = "vu_trial_banner_dismissed";
const TRIAL_TOTAL_DAYS = 7;

function isDismissedToday() {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const today = new Date().toISOString().slice(0, 10);
    return v === today;
}

// Returns "3d 4h", "12h", "45m" depending on the magnitude.
function formatTrialRemaining(trialEndIso) {
    if (!trialEndIso) return null;
    const end = new Date(trialEndIso).getTime();
    if (!end) return null;
    const ms = end - Date.now();
    if (ms <= 0) return null;
    const totalMins = Math.floor(ms / 60000);
    const days = Math.floor(totalMins / (60 * 24));
    const hours = Math.floor((totalMins - days * 60 * 24) / 60);
    const mins = totalMins - days * 60 * 24 - hours * 60;
    if (days >= 1) return hours ? `${days}d ${hours}h` : `${days}d`;
    if (hours >= 1) return mins ? `${hours}h ${mins}m` : `${hours}h`;
    return `${mins}m`;
}

export default function TrialBanner() {
    const { t } = useTranslation();
    const { fmtCurrency } = useFormat();
    const { user } = useAuth();
    const [bs, setBs] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [now, setNow] = useState(Date.now()); // tick for live countdown when urgent

    useEffect(() => {
        if (user?.role) api.get("/billing/status").then(r => setBs(r.data)).catch(() => {});
        setDismissed(isDismissedToday());
    }, [user]);

    // Live tick once a minute when in urgent state so the countdown updates without reload
    useEffect(() => {
        if (!bs) return;
        const days = bs.days_left || 0;
        if (bs.subscription_status === "active") return;
        if (days > 3) return;
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, [bs]);

    if (!bs || bs.subscription_status === "active") return null;
    const days = bs.days_left || 0;
    const cancelled = bs.subscription_status === "cancelled";
    const expired = !cancelled && days === 0;
    const urgent = !cancelled && days > 0 && days <= 3;
    const soft = !cancelled && days >= 4;

    if (dismissed && soft) return null;

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10));
        setDismissed(true);
    };

    const remaining = formatTrialRemaining(bs.trial_end);
    const usedDays = Math.max(0, TRIAL_TOTAL_DAYS - days);
    const pctUsed = Math.min(100, Math.round((usedDays / TRIAL_TOTAL_DAYS) * 100));

    // Tone + tier
    const tier = cancelled ? "cancelled" : expired ? "expired" : urgent ? "urgent" : "soft";

    const tone = {
        soft:      { border: "var(--brand-purple)",  bar: "var(--brand-purple)",  Icon: Sparkles },
        urgent:    { border: "var(--brand-orange)",  bar: "var(--brand-orange)",  Icon: Clock },
        expired:   { border: "var(--brand-magenta)", bar: "var(--brand-magenta)", Icon: AlertTriangle },
        cancelled: { border: "var(--brand-magenta)", bar: "var(--brand-magenta)", Icon: AlertTriangle },
    }[tier];

    const headline = {
        soft:      t("trial.daysLeftSoft", { count: days }),
        urgent:    days === 1 ? t("trial.daysLeftOne") : t("trial.daysLeftUrgent", { count: days }),
        expired:   t("trial.trialEnded"),
        cancelled: t("trial.cancelled"),
    }[tier];

    // Stripe still charges in GBP — pin the currency but route the display
    // through the shared formatter for locale-aware digit grouping.
    const priceStr = fmtCurrency(bs.plan_price_gbp || 0, "GBP");

    const subcopy = {
        soft:      remaining ? t("trial.softSubcopyWithRemaining", { remaining }) : t("trial.softSubcopy"),
        urgent:    remaining ? t("trial.urgentSubcopyWithRemaining", { remaining }) : t("trial.urgentSubcopy"),
        expired:   t("trial.expiredSubcopy", { price: priceStr }),
        cancelled: t("trial.cancelledSubcopy", { price: priceStr }),
    }[tier];

    const ctaLabel = expired || cancelled ? t("trial.ctaReactivate") : urgent ? t("trial.ctaAddCard") : t("trial.ctaSubscribe");

    return (
        <div
            data-testid="trial-banner"
            data-trial-urgency={tier}
            className="card-warm mb-4 border-l-4 overflow-hidden"
            style={{ borderLeftColor: tone.border }}
        >
            <div className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
                <tone.Icon
                    size={16}
                    className={`shrink-0 ${urgent ? "animate-pulse" : ""}`}
                    style={{ color: tone.border }}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-script text-lg" style={{ color: tone.border }} data-testid="trial-banner-headline">
                            {headline}
                        </span>
                        {!expired && !cancelled && (
                            <span className="text-[11px] text-[var(--text-ink)]/55 tabular-nums" data-testid="trial-banner-progress-label">
                                {t("trial.dayOfTotal", { day: Math.min(usedDays + 1, TRIAL_TOTAL_DAYS), total: TRIAL_TOTAL_DAYS })}
                            </span>
                        )}
                    </div>
                    <div className="text-[var(--text-ink)]/70 text-xs mt-0.5" data-testid="trial-banner-subcopy">
                        {subcopy}
                    </div>
                </div>
                <Link to="/billing" data-testid="trial-banner-cta" className="btn-primary text-xs whitespace-nowrap">{ctaLabel}</Link>
                {soft && (
                    <button onClick={dismiss} aria-label={t("trial.dismissAria")}
                            data-testid="trial-banner-dismiss"
                            className="w-8 h-8 grid place-items-center rounded-full text-[var(--text-ink)]/45 hover:text-[var(--brand-magenta)] hover:bg-[rgba(26,19,24,0.05)] active:bg-[rgba(26,19,24,0.08)] transition shrink-0">
                        <X size={16} />
                    </button>
                )}
            </div>
            {!expired && !cancelled && (
                <div className="h-1 w-full bg-[rgba(26,19,24,0.06)]" data-testid="trial-banner-progress">
                    <div
                        className="h-full transition-[width] duration-500"
                        style={{ width: `${pctUsed}%`, background: tone.bar }}
                    />
                </div>
            )}
            {/* keep `now` referenced so React re-renders on tick without lint warnings */}
            <span className="hidden" aria-hidden="true">{now}</span>
        </div>
    );
}
